import { useEffect, useRef } from 'react';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { ViewManager } from '~/engine/view/ViewManager';
import { flushGridContourEdits } from '~/hooks/gridContourFlush';
import { localization } from '~/i18n';

/**
 * Persists grid contour edits when the grid host unmounts.
 *
 * React tears a deleted tree down parent-first, so this cleanup runs
 * BEFORE the tile renderers release their Environment refcounts and the
 * SDF textures are freed (verified empirically in this hook's test).
 * The flush is the explicit persistence boundary; the Environment
 * dispose path only backstops it.
 *
 * After the children have released (post-commit microtask) it also
 * asserts that no grid views remain registered — observability for
 * leaked tiles, never a throw.
 */
export function useGridTeardownFlush(
    registry: DatasetEnvironmentRegistry,
    viewManager: ViewManager | null
): void {
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            flushGridContourEdits(
                registry,
                localization.t('contouring.gridSaveFailed.editsMayBeLost')
            );
            queueMicrotask(() => {
                // Remounted (StrictMode / dep change) — not a teardown.
                if (mountedRef.current) {
                    return;
                }
                if (ViewManager.gridViewsRegistered) {
                    const residue = viewManager?.gridViewCount;
                    console.error(
                        `Grid teardown left ${residue ?? 'unknown'} grid ` +
                            'view(s) registered in the ViewManager.'
                    );
                }
            });
        };
    }, [registry, viewManager]);
}
